import { useQuery, skipToken } from '@tanstack/react-query';
import { isPermissionRefused, type BoxMarks } from '@citibox-panel/api-client';

export interface UseBoxMarks {
  marks: BoxMarks | undefined;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

// The unsync-mark level of a «buzón», keyed by BOX id alone — unlike ['box-status', boxId,
// deliveryId], which needs both because its answer is relative to the delivery on screen. Marks
// belong to the «buzón», so two deliveries stored in it read the same level and share the entry.
//
// Skipped (skipToken) until the region is unfolded AND a box is known, so opening the «Entrega»
// detail costs no request and the first unfold is the lazy read. The hook stays MOUNTED while the
// delivery has a «buzón», so its cache survives a collapse→expand.
//
// staleTime: 0 overrides the client's 5-minute default, for the reason the codes do: marks accrue
// from sequences the panel never sees, so a cached level inside that window would have the agent
// deciding on a number the server has already moved.
//
// retry: false overrides the client default too. It was first set because the endpoint did not
// exist and every detail with a «buzón» got a 404 no retry could fix; app-service shipped it
// (CBX-11149) and the reason now stands on the delivered contract: the failures it can answer are
// 404 (unknown box), 401 and 403 — all verdicts, none of which a second attempt changes. The
// strip's «Reintentar» is the retry, driven by the agent rather than spent silently.
export function useBoxMarks(
  boxId: string | undefined,
  open: boolean,
  fetchBoxMarks: (boxId: string) => Promise<BoxMarks>,
): UseBoxMarks {
  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['box-marks', boxId],
    queryFn: open && boxId !== undefined ? () => fetchBoxMarks(boxId) : skipToken,
    staleTime: 0,
    retry: false,
  });
  // A permission refusal is the host's to show: it replaces the whole view, so reporting it here
  // as a failed read would only paint the inline strip under the access screen.
  return {
    marks: data,
    isPending,
    isFetching,
    isError: isError && !isPermissionRefused(error),
    refetch: () => void refetch(),
  };
}
